import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'clsx';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Text, TextType } from 'ui-components/components/text';

import { CompareFeaturesSection } from './compare-features-section/compare-features-section';
import { messages } from './messages';
import styles from './new-lineup-plan-grid.module.less';
import { PlanDetails } from './plan-details/plan-details';
import { BillingPlanIds } from '../../../../../enums/billing-plan-ids';
import { useOnce } from '../../../../../hooks/use-once';
import Global from '../../../../../store/global-selectors';
import { SegmentedControl } from '@mantine/core';
import { useManageSubscriptionDrawerContext } from '../../context/manage-subscription-drawer-context';
import { useGetNumberOfExtraUserSeats } from '../../hooks/use-get-number-of-extra-user-seats';
import { useCurrentSubscriptionShipmentLimit } from '../../hooks/use-plan-details';
import { useTrackEvent, PageEvents } from '../../hooks/use-track-event';
import { findAnnualPlanId, getInitialShipmentLimitForPlan, findMonthlyPlanId } from '../../utils/plan-utilities';
import { FlexRow } from 'ui-components/components/flex';
import { useShouldShowAnnualToggle, useMaxAnnualSavingsPercent } from '../../hooks/use-coupon';

/**
 * @typedef {import('../../../../../prop-types/subscription-data-types.d.ts').BillingPlan} BillingPlan
 * @typedef {import('../../../../../prop-types/subscription-data-types.d.ts').SubscriptionData} SubscriptionData
 */

/**
 * @param {BillingPlan[]} plans
 * @returns {boolean}
 */
export const hasAnnualPlans = (plans) => plans.some((plan) => plan.isOnAnnualBilling);

/**
 * @param {BillingPlan[]} plans
 * @param {boolean} showAnnual
 * @returns {BillingPlan[]}
 */
const filterPlansByAnnualState = (plans, showAnnual) => {
  if (showAnnual) {
    return plans.filter((plan) => plan.isOnAnnualBilling);
  }
  return plans.filter((plan) => !plan.isOnAnnualBilling);
};

const DefaultPlanId = BillingPlanIds.Standard2025Q2.toString();
const MONTHLY_LABEL = 'monthly';
const ANNUAL_LABEL = 'annual';

/**
 * @param {object} props
 * @param {(billingPlanId: string) => void} props.onSelectPlan
 * @param {boolean} [props.isMergedWithTabs]
 * @returns {React.JSX.Element}
 */
export const NewLineupPlanGrid = ({ onSelectPlan, isMergedWithTabs }) => {
  const intl = useIntl();
  const {
    selectedPlanId,
    setSelectedPlanId,
    setDesiredNumberOfAdditionalAnalyticsLicenses,
    setDesiredNumberOfAdditionalUsers,
    setDesiredShipmentLimit,
    initialChangePayload,
    showAnnualPlans: userWantsAnnualPlans,
    setShowAnnualPlans,
    isOnAnnualBilling,
    isMigratingToNewestPlans,
    recommendedPlanIds,
    referencePlans,
  } = useManageSubscriptionDrawerContext();

  const currentSubscriptionShipmentLimit = useCurrentSubscriptionShipmentLimit();

  /** @type {SubscriptionData} */
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const basePlans = useMemo(
    () => (isMigratingToNewestPlans ? subscriptionData.defaultProfilePlans : subscriptionData.allPlans) ?? subscriptionData.allPlans,
    [isMigratingToNewestPlans, subscriptionData.defaultProfilePlans, subscriptionData.allPlans],
  );

  const hasAnyAnnualPlans = useMemo(() => hasAnnualPlans(basePlans), [basePlans]);

  const shouldShowAnnualToggle = useShouldShowAnnualToggle(referencePlans);
  const maxSavingsPercent = useMaxAnnualSavingsPercent(referencePlans);

  const showAnnualPlans = useMemo(
    () => (shouldShowAnnualToggle ? userWantsAnnualPlans : isOnAnnualBilling),
    [shouldShowAnnualToggle, userWantsAnnualPlans, isOnAnnualBilling],
  );

  // Only filter by annual state if annual plans exist; otherwise show all plans
  const displayedPlans = useMemo(() => {
    let plans = hasAnyAnnualPlans ? filterPlansByAnnualState(basePlans, showAnnualPlans) : basePlans;

    // Filter out Free plan for migrating sellers
    if (isMigratingToNewestPlans) {
      plans = plans.filter((plan) => plan.billingPlanId !== BillingPlanIds.Free2025Q2.toString());
    }

    return plans;
  }, [basePlans, showAnnualPlans, hasAnyAnnualPlans, isMigratingToNewestPlans]);

  const currentBillingPlanId = subscriptionData?.currentBillingPlan?.billingPlanId;
  const isCurrentBillingPlanTrial = currentBillingPlanId === BillingPlanIds.Trial.toString();

  const baseShipmentLimits = useMemo(() => {
    return basePlans.reduce((acc, plan) => {
      const planId = plan.billingPlanId;

      if (planId === initialChangePayload?.selectedPlanId && initialChangePayload?.initialDesiredShipmentLimit) {
        return {
          ...acc,
          [planId]: initialChangePayload.initialDesiredShipmentLimit,
        };
      }

      const shipmentLimitForPlan = getInitialShipmentLimitForPlan(plan, currentBillingPlanId, currentSubscriptionShipmentLimit);

      return {
        ...acc,
        [planId]: shipmentLimitForPlan,
      };
    }, {});
  }, [basePlans, initialChangePayload, currentBillingPlanId, currentSubscriptionShipmentLimit]);

  const [selectedShipmentLimits, setSelectedShipmentLimits] = useState({});
  const effectiveShipmentLimits = useMemo(
    () => ({ ...baseShipmentLimits, ...selectedShipmentLimits }),
    [baseShipmentLimits, selectedShipmentLimits],
  );

  const currentPlanAdditionalUserCount = useGetNumberOfExtraUserSeats();

  const baseAdditionalUsers = useMemo(() => {
    return basePlans.reduce((acc, plan) => {
      const planId = plan.billingPlanId;

      if (planId === initialChangePayload?.selectedPlanId && initialChangePayload?.initialDesiredNumberOfAdditionalUsers) {
        return {
          ...acc,
          [planId]: initialChangePayload.initialDesiredNumberOfAdditionalUsers,
        };
      }

      const initialAdditionalUserCount = planId === currentBillingPlanId ? currentPlanAdditionalUserCount : 0;

      return {
        ...acc,
        [planId]: initialAdditionalUserCount,
      };
    }, {});
  }, [basePlans, initialChangePayload, currentBillingPlanId, currentPlanAdditionalUserCount]);

  const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState({});
  const effectiveAdditionalUsers = useMemo(
    () => ({ ...baseAdditionalUsers, ...selectedAdditionalUsers }),
    [baseAdditionalUsers, selectedAdditionalUsers],
  );

  const currentPlanAdditionalAnalyticsLicenseCount = subscriptionData?.currentBillingPlan?.additionalAnalyticsLicenses ?? 0;
  const currentTotalAnalyticsLicenses = subscriptionData?.currentBillingPlan?.analyticsLicenseLimit?.limit ?? 0;

  const baseAdditionalAnalyticsLicenses = useMemo(() => {
    return basePlans.reduce((acc, plan) => {
      const planId = plan.billingPlanId;

      if (planId === currentBillingPlanId) {
        return { ...acc, [planId]: currentPlanAdditionalAnalyticsLicenseCount };
      }

      const planIncludedLicenses = plan.analyticsLicenseLimit?.limit ?? 0;
      const initialCount = planIncludedLicenses > 0 ? Math.max(0, currentTotalAnalyticsLicenses - planIncludedLicenses) : 0;

      return { ...acc, [planId]: initialCount };
    }, {});
  }, [basePlans, currentBillingPlanId, currentPlanAdditionalAnalyticsLicenseCount, currentTotalAnalyticsLicenses]);

  const [selectedAdditionalAnalyticsLicenses, setSelectedAdditionalAnalyticsLicenses] = useState({});
  const effectiveAdditionalAnalyticsLicenses = useMemo(
    () => ({ ...baseAdditionalAnalyticsLicenses, ...selectedAdditionalAnalyticsLicenses }),
    [baseAdditionalAnalyticsLicenses, selectedAdditionalAnalyticsLicenses],
  );

  const updateSelectedPlan = useCallback(
    (planId) => {
      let newSelectedPlanId;
      if (planId !== selectedPlanId) {
        // new plan selected
        newSelectedPlanId = planId;
      } else if (!isCurrentBillingPlanTrial && currentBillingPlanId) {
        // plan deselected, with selectable current plan
        newSelectedPlanId = currentBillingPlanId;
      } else {
        newSelectedPlanId = DefaultPlanId; // plan deselected, with non-selectable current plan
      }

      onSelectPlan(newSelectedPlanId);
      setDesiredShipmentLimit(effectiveShipmentLimits[newSelectedPlanId]);
      setDesiredNumberOfAdditionalUsers(effectiveAdditionalUsers[newSelectedPlanId]);
      setDesiredNumberOfAdditionalAnalyticsLicenses(effectiveAdditionalAnalyticsLicenses[newSelectedPlanId] ?? 0);
    },
    [
      effectiveAdditionalAnalyticsLicenses,
      effectiveAdditionalUsers,
      currentBillingPlanId,
      isCurrentBillingPlanTrial,
      onSelectPlan,
      selectedPlanId,
      effectiveShipmentLimits,
      setDesiredNumberOfAdditionalAnalyticsLicenses,
      setDesiredNumberOfAdditionalUsers,
      setDesiredShipmentLimit,
    ],
  );

  const hasValidBillingPlanId = subscriptionData?.currentBillingPlan?.billingPlanId ?? false;

  useOnce(() => {
    const shouldAutoSelect = isCurrentBillingPlanTrial || !hasValidBillingPlanId || isMigratingToNewestPlans;
    if (shouldAutoSelect && !initialChangePayload?.selectedPlanId) {
      const planIdToSelect = recommendedPlanIds[0] ?? DefaultPlanId;
      updateSelectedPlan(planIdToSelect);
    } else if (initialChangePayload?.selectedPlanId) {
      const preSelectedPlanId = initialChangePayload.selectedPlanId;
      onSelectPlan(preSelectedPlanId);
      setDesiredShipmentLimit(effectiveShipmentLimits[preSelectedPlanId]);
    }
  });

  const onChangePlanShipmentLimit = useCallback(
    (planId, count) => {
      setSelectedShipmentLimits((prev) => ({
        ...prev,
        [planId]: count,
      }));

      if (planId === selectedPlanId) {
        setDesiredShipmentLimit(count);
      }
    },
    [selectedPlanId, setDesiredShipmentLimit],
  );

  const onChangeUserCount = useCallback(
    (planId, additionalUserCount) => {
      setSelectedAdditionalUsers((prev) => ({
        ...prev,
        [planId]: additionalUserCount,
      }));

      if (planId === selectedPlanId) {
        setDesiredNumberOfAdditionalUsers(additionalUserCount);
      }
    },
    [selectedPlanId, setDesiredNumberOfAdditionalUsers],
  );

  const onChangeAnalyticsLicenseCount = useCallback(
    (planId, additionalLicenseCount) => {
      setSelectedAdditionalAnalyticsLicenses((prev) => ({
        ...prev,
        [planId]: additionalLicenseCount,
      }));

      if (planId === selectedPlanId) {
        setDesiredNumberOfAdditionalAnalyticsLicenses(additionalLicenseCount);
      }
    },
    [selectedPlanId, setDesiredNumberOfAdditionalAnalyticsLicenses],
  );

  const copyPlanSettingsToEquivalent = useCallback(
    (fromPlanId, toPlanId) => {
      const currentShipmentLimit = effectiveShipmentLimits[fromPlanId];
      const currentUserCount = effectiveAdditionalUsers[fromPlanId];
      const currentAnalyticsLicenseCount = effectiveAdditionalAnalyticsLicenses[fromPlanId];

      if (currentShipmentLimit !== undefined) {
        setSelectedShipmentLimits((prev) => ({ ...prev, [toPlanId]: currentShipmentLimit }));
        setDesiredShipmentLimit(currentShipmentLimit);
      }
      if (currentUserCount !== undefined) {
        setSelectedAdditionalUsers((prev) => ({ ...prev, [toPlanId]: currentUserCount }));
        setDesiredNumberOfAdditionalUsers(currentUserCount);
      }
      if (currentAnalyticsLicenseCount !== undefined) {
        setSelectedAdditionalAnalyticsLicenses((prev) => ({ ...prev, [toPlanId]: currentAnalyticsLicenseCount }));
        setDesiredNumberOfAdditionalAnalyticsLicenses(currentAnalyticsLicenseCount);
      }

      setSelectedPlanId(toPlanId);
    },
    [
      effectiveAdditionalAnalyticsLicenses,
      effectiveShipmentLimits,
      effectiveAdditionalUsers,
      setSelectedAdditionalAnalyticsLicenses,
      setDesiredNumberOfAdditionalAnalyticsLicenses,
      setSelectedShipmentLimits,
      setDesiredShipmentLimit,
      setSelectedAdditionalUsers,
      setDesiredNumberOfAdditionalUsers,
      setSelectedPlanId,
    ],
  );

  const handleAnnualToggleChange = useCallback(
    (checked) => {
      const equivalentPlanId = checked
        ? findAnnualPlanId(selectedPlanId, referencePlans)
        : findMonthlyPlanId(selectedPlanId, referencePlans);

      const isEquivalentInBasePlans = equivalentPlanId && basePlans.some((p) => p.billingPlanId === equivalentPlanId);

      if (isEquivalentInBasePlans) {
        copyPlanSettingsToEquivalent(selectedPlanId, equivalentPlanId);
      } else if (isCurrentBillingPlanTrial) {
        const preferredDefault = checked ? (findAnnualPlanId(DefaultPlanId, referencePlans) ?? DefaultPlanId) : DefaultPlanId;
        const newDisplayedPlans = filterPlansByAnnualState(basePlans, checked);
        const isPreferredInGrid = newDisplayedPlans.some((p) => p.billingPlanId === preferredDefault);
        updateSelectedPlan(isPreferredInGrid ? preferredDefault : newDisplayedPlans[0]?.billingPlanId);
      }

      setShowAnnualPlans(checked);
    },
    [
      selectedPlanId,
      referencePlans,
      basePlans,
      isCurrentBillingPlanTrial,
      copyPlanSettingsToEquivalent,
      setShowAnnualPlans,
      updateSelectedPlan,
    ],
  );

  const trackAnnualBillingToggle = useTrackEvent(PageEvents.ANNUAL_BILLING_TOGGLE);

  const handleAnnualToggleChangeFromSegment = useCallback(
    (value) => {
      const isSelectingAnnual = value === ANNUAL_LABEL;
      trackAnnualBillingToggle({
        selected_view: isSelectingAnnual ? 'annual' : 'monthly',
        previous_view: isSelectingAnnual ? 'monthly' : 'annual',
        billingPlanId: currentBillingPlanId,
        shipmentVolume: currentSubscriptionShipmentLimit,
        maxSavingsPercent,
      });
      handleAnnualToggleChange(isSelectingAnnual);
    },
    [handleAnnualToggleChange, trackAnnualBillingToggle, currentBillingPlanId, currentSubscriptionShipmentLimit, maxSavingsPercent],
  );

  const annualSegmentLabel = useMemo(
    () => (
      <span className={styles['annual-segment-label']}>
        {intl.formatMessage(messages.annualBillingOption)}
        <span className={styles['savings-badge']}>{intl.formatMessage(messages.annualSavingsBadge, { percent: maxSavingsPercent })}</span>
      </span>
    ),
    [intl, maxSavingsPercent],
  );

  // Sync selected plan when toggling between annual/monthly views
  useEffect(() => {
    if (displayedPlans.length === 0) {
      return;
    }

    const isSelectedPlanInDisplayedPlans = selectedPlanId && displayedPlans.some((plan) => plan.billingPlanId === selectedPlanId);

    // If selected plan is visible, no action needed
    if (isSelectedPlanInDisplayedPlans) {
      return;
    }

    // Try to find the equivalent plan in the current view (annual↔monthly conversion)
    const equivalentPlanId = showAnnualPlans
      ? findAnnualPlanId(selectedPlanId, referencePlans)
      : findMonthlyPlanId(selectedPlanId, referencePlans);
    const isEquivalentInGrid = equivalentPlanId && displayedPlans.some((plan) => plan.billingPlanId === equivalentPlanId);

    if (isEquivalentInGrid) {
      copyPlanSettingsToEquivalent(selectedPlanId, equivalentPlanId);
      return;
    }

    // No equivalent found - only force selection for trial users who must pick a plan
    // Non-trial users: leave as-is, Plan Summary shows current subscription data
    if (!isCurrentBillingPlanTrial) {
      return;
    }

    // Fall back to Standard plan for the current view, then first plan
    const preferredDefault = showAnnualPlans ? (findAnnualPlanId(DefaultPlanId, referencePlans) ?? DefaultPlanId) : DefaultPlanId;
    const isPreferredInGrid = displayedPlans.some((plan) => plan.billingPlanId === preferredDefault);

    updateSelectedPlan(isPreferredInGrid ? preferredDefault : displayedPlans[0].billingPlanId);
  }, [
    displayedPlans,
    selectedPlanId,
    updateSelectedPlan,
    isCurrentBillingPlanTrial,
    showAnnualPlans,
    copyPlanSettingsToEquivalent,
    referencePlans,
  ]);

  const numberOfPlans = displayedPlans.length;

  const filteredSubscriptionData = useMemo(
    () => ({
      ...subscriptionData,
      allPlans: displayedPlans,
    }),
    [subscriptionData, displayedPlans],
  );

  return (
    <>
      <div
        className={classnames(styles['new-lineup-plan-grid-card'], {
          [styles['merged-with-tabs']]: isMergedWithTabs,
        })}
      >
        {shouldShowAnnualToggle ? (
          <FlexRow className={styles['annual-toggle']}>
            <SegmentedControl
              data={[
                { label: intl.formatMessage(messages.monthlyBillingOption), value: MONTHLY_LABEL },
                { label: annualSegmentLabel, value: ANNUAL_LABEL },
              ]}
              onChange={handleAnnualToggleChangeFromSegment}
              value={showAnnualPlans ? ANNUAL_LABEL : MONTHLY_LABEL}
            />
          </FlexRow>
        ) : null}
        {isOnAnnualBilling && !showAnnualPlans && (
          <BannerMessageV2
            className={styles['monthly-switch-error-banner']}
            content={<Text textType={TextType.Paragraph} value={messages.unableToChangePlanBannerBody} />}
            header={<Text textType={TextType.Label} value={messages.unableToChangePlanBannerHeader} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Error}
          />
        )}
        <div
          className={styles['new-lineup-plan-grid']}
          style={{
            gridTemplateColumns: `repeat(${numberOfPlans}, minmax(160px, 1fr))`,
          }}
        >
          <div className={styles['plan-headers']}>
            {displayedPlans.map((plan, index, allPlans) => {
              const previousTierPlan = index === 0 ? null : allPlans[index - 1];

              const previousTierPlanName = previousTierPlan?.name;

              return (
                <PlanDetails
                  key={plan.billingPlanId}
                  additionalAnalyticsLicenseCount={effectiveAdditionalAnalyticsLicenses[plan.billingPlanId] ?? 0}
                  additionalUserCount={effectiveAdditionalUsers[plan.billingPlanId]}
                  isCurrentBillingPlanTrial={isCurrentBillingPlanTrial}
                  isCurrentPlan={currentBillingPlanId === plan.billingPlanId}
                  isDeselectAllowed={Boolean(currentBillingPlanId) && !isCurrentBillingPlanTrial}
                  isRecommendedPlan={recommendedPlanIds.includes(plan.billingPlanId)}
                  isSelectedPlan={selectedPlanId === plan.billingPlanId}
                  onChangeAnalyticsLicenseCount={onChangeAnalyticsLicenseCount}
                  onChangeSelectedShipmentLimit={onChangePlanShipmentLimit}
                  onChangeUserCount={onChangeUserCount}
                  onSelectPlan={updateSelectedPlan}
                  plan={plan}
                  previousTierPlanName={previousTierPlanName}
                  selectedShipmentLimit={effectiveShipmentLimits[plan.billingPlanId] || 0}
                  showAnnualPlans={showAnnualPlans && hasAnyAnnualPlans}
                />
              );
            })}
          </div>
        </div>
      </div>
      <CompareFeaturesSection
        currentBillingPlanId={currentBillingPlanId}
        plans={displayedPlans}
        subscriptionData={filteredSubscriptionData}
      />
    </>
  );
};
